// Conditional logic. Mirrors api/internal/survey/answers.go; the shared
// fixtures/conditions.json cases are run against both implementations.
import type { AnswerValue, Condition, Definition } from './types';

const DATE = /^\d{4}-\d{2}-\d{2}$/;

export function valueString(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return String(v);
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  return JSON.stringify(v);
}

export function isAnswered(v: unknown): boolean {
  if (v === null || v === undefined) return false;
  if (typeof v === 'string') return v.trim() !== '';
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object') return Object.values(v as object).some(isAnswered);
  return true;
}

/** The part of an answer a condition looks at: a matrix row, sum item or form field when `row` is set. */
export function conditionAnswer(answer: AnswerValue | undefined, row: string | undefined): AnswerValue | undefined {
  if (!row) return answer;
  if (answer && typeof answer === 'object' && !Array.isArray(answer)) return answer[row] ?? null;
  return null;
}

function asNumber(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v.trim());
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function compare(answer: unknown, target: unknown): number | null {
  const a = asNumber(answer);
  const b = asNumber(target);
  if (a !== null && b !== null) return a < b ? -1 : a > b ? 1 : 0;
  const as = valueString(answer);
  const bs = valueString(target);
  if (DATE.test(as) && DATE.test(bs)) return as < bs ? -1 : as > bs ? 1 : 0;
  return null;
}

export function evalCondition(c: Condition, raw: AnswerValue | undefined): boolean {
  const answer = conditionAnswer(raw, c.row);
  const target = valueString(c.value);
  switch (c.operator) {
    case 'answered':
      return isAnswered(answer);
    case 'not_answered':
      return !isAnswered(answer);
    case 'equals':
    case 'not_equals': {
      let eq = false;
      if (Array.isArray(answer)) eq = answer.some((e) => valueString(e) === target);
      else if (isAnswered(answer)) eq = valueString(answer) === target;
      return c.operator === 'equals' ? eq : !eq;
    }
    case 'contains':
    case 'not_contains': {
      let has = false;
      if (Array.isArray(answer)) has = answer.some((e) => valueString(e) === target);
      else if (isAnswered(answer)) has = valueString(answer).toLowerCase().includes(target.toLowerCase());
      return c.operator === 'contains' ? has : !has;
    }
    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte': {
      if (!isAnswered(answer)) return false;
      const cmp = compare(answer, c.value);
      if (cmp === null) return false;
      if (c.operator === 'gt') return cmp > 0;
      if (c.operator === 'gte') return cmp >= 0;
      if (c.operator === 'lt') return cmp < 0;
      return cmp <= 0;
    }
  }
  return false;
}

/** Question ids referenced by any condition, carry-forward or loop. */
export function logicSources(def: Definition): Set<string> {
  const ids = new Set<string>();
  for (const q of def.questions) {
    for (const c of q.visibleIf?.conditions ?? []) ids.add(c.questionId);
    if (q.carryForward) ids.add(q.carryForward.questionId);
  }
  for (const o of Object.values(def.settings.sectionOptions ?? {})) if (o.loopOver) ids.add(o.loopOver);
  return ids;
}
